import React, { forwardRef, useEffect, useCallback, useRef } from 'react';
import 'swiper/css/effect-fade'; //Added by Aarif
import PropTypes from 'prop-types';
import cn from 'classnames';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Thumbs, EffectFade, Autoplay } from 'swiper/modules';
import SwiperCore from 'swiper';
import { SliderWrap } from '@/components/common/swiper/swiper.style';

// Install Swiper modules
SwiperCore.use([Navigation, Pagination, Thumbs, EffectFade, Autoplay]);

// eslint-disable-next-line react/display-name
const Slider = forwardRef((props, ref) => {
    const {
        onSetTransition,
        onSlideChange,
        onProgress,
        touchStart,
        onSwiperInit,
        onBeforeInit,
        slidesPerGroup,
        children,
        effect,
        slidesPerView = 1,
        pagination = false,
        nextEl = null,
        prevEl = null,
        spaceBetween = 0,
        loop = false,
        autoplay = false,
        speed = 2500,
        grabCursor = false,
        watchSlidesProgress = false,
        mousewheelControl = false,
        keyboardControl = false,
        animate,
        settings = {},
        breakpoints = {},
        className,
        on,
        direction,
        parentclass,
    } = props;

    const swiperRef = useRef(null);
    const isDestroyedRef = useRef(false);

    // Cleanup function to prevent memory leaks
    const cleanup = useCallback(() => {
        if (swiperRef.current && !isDestroyedRef.current) {
            try {
                // Stop autoplay if it exists
                if (swiperRef.current.autoplay) {
                    swiperRef.current.autoplay.stop();
                }
                
                // Remove event listeners
                if (swiperRef.current.off) {
                    swiperRef.current.off('slideChange');
                    swiperRef.current.off('progress');
                    swiperRef.current.off('touchStart');
                    swiperRef.current.off('setTransition');
                }
                
                // Destroy swiper instance
                if (swiperRef.current.destroy) {
                    swiperRef.current.destroy(true, true);
                }
                
                isDestroyedRef.current = true;
            } catch (error) {
                console.warn('Error during Swiper cleanup:', error);
            }
        }
    }, []);

    // Handle swiper initialization with cleanup
    const handleSwiperInit = useCallback((swiper) => {
        if (isDestroyedRef.current) return;
        
        swiperRef.current = swiper;
        
        // Setup event listeners with cleanup tracking
        if (onSlideChange) {
            swiper.on('slideChange', (swiperInstance) => {
                if (!isDestroyedRef.current) {
                    onSlideChange(swiperInstance);
                }
            });
        }
        
        if (onProgress) {
            swiper.on('progress', (swiperInstance, progress) => {
                if (!isDestroyedRef.current) {
                    onProgress(swiperInstance, progress);
                }
            });
        }
        
        if (touchStart) {
            swiper.on('touchStart', (swiperInstance, event) => {
                if (!isDestroyedRef.current) {
                    touchStart(swiperInstance, event);
                }
            });
        }
        
        if (onSetTransition) {
            swiper.on('setTransition', (swiperInstance, transition) => {
                if (!isDestroyedRef.current) {
                    onSetTransition(swiperInstance, transition);
                }
            });
        }
        
        // Call the original callbacks
        if (onSwiperInit) {
            onSwiperInit(swiper);
        }
        
        if (onBeforeInit) {
            onBeforeInit(swiper);
        }
    }, [onSlideChange, onProgress, touchStart, onSetTransition, onSwiperInit, onBeforeInit]);

    // Create Swiper options object with all necessary settings
    const sliderOptions = {
        slidesPerView,
        pagination: pagination === true ? { clickable: true } : pagination,
        slidesPerGroup,
        navigation: {
            nextEl,
            prevEl,
        },
        spaceBetween,
        loop,
        autoplay,
        speed,
        grabCursor,
        watchSlidesProgress,
        mousewheel: mousewheelControl,
        keyboard: keyboardControl,
        breakpoints,
        effect,
        on,
        direction,
        // Memory leak prevention options
        watchOverflow: true,
        preventClicks: true,
        preventClicksPropagation: true,
        touchReleaseOnEdges: true,
        updateOnWindowResize: true,
        resizeObserver: true,
        observer: true,
        observeParents: true,
        observeSlideChildren: false,
        ...settings,
    };

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            cleanup();
        };
    }, [cleanup]);

    // Effect to initialize Swiper instance when the component is mounted
    useEffect(() => {
        if (ref?.current?.swiper) {
            handleSwiperInit(ref.current.swiper);
        }
    }, [handleSwiperInit, ref]);

    return (
        <SliderWrap animate={animate} className={cn(parentclass)}>
            <Swiper 
                {...sliderOptions} 
                className={cn(className)} 
                ref={ref}
                onSwiper={handleSwiperInit}
                modules={[Navigation, Pagination, Thumbs, EffectFade, Autoplay]}
            >
                {children}
            </Swiper>
        </SliderWrap>
    );
});

// Define prop types for the Slider component
Slider.propTypes = {
    onSetTransition: PropTypes.func,
    onSlideChange: PropTypes.func,
    onProgress: PropTypes.func,
    touchStart: PropTypes.func,
    onBeforeInit: PropTypes.func,
    onSwiperInit: PropTypes.func,
    on: PropTypes.func,
    children: PropTypes.node.isRequired,
    slidesPerView: PropTypes.number,
    pagination: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
    nextEl: PropTypes.string,
    effect: PropTypes.string,
    prevEl: PropTypes.string,
    spaceBetween: PropTypes.number,
    slidesPerGroup: PropTypes.number,
    loop: PropTypes.bool,
    autoplay: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
    speed: PropTypes.number,
    grabCursor: PropTypes.bool,
    watchSlidesProgress: PropTypes.bool,
    mousewheelControl: PropTypes.bool,
    keyboardControl: PropTypes.bool,
    animate: PropTypes.bool,
    settings: PropTypes.object,
    breakpoints: PropTypes.object, // Define the prop type for breakpoints
    className: PropTypes.string,
    direction: PropTypes.string,
};

// Export the SwiperSlide component for use
export { SwiperSlide as Slide };

// Memoize the Slider component to avoid unnecessary re-renders
export default React.memo(Slider);
